import { createSignal } from "solid-js";
import type { ShoppingItem } from "@mealplan/powersync";

import { closestSelector } from "@/lib/gestures/guards";
import { applyRubberBand, createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";
import { SHOPPING_ROW_SWIPE } from "@/lib/gestures/presets";

interface SwipeGesturesOptions {
  item: () => ShoppingItem;
  isChecked: () => boolean;
  onToggle: (item: ShoppingItem, checked: boolean) => void;
  onDelete: (id: string) => void;
  getSurfaceRef: () => HTMLElement | undefined;
}

const {
  thresholdPx: THRESHOLD_PX,
  commitThresholdPx: COMMIT_THRESHOLD_PX,
  rubberBandLimitPx: RUBBER_BAND_LIMIT,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  hapticMs: HAPTIC_VIBRATION_MS,
  commitAnimationMs: COMMIT_ANIMATION_MS,
  cancelAnimationMs: CANCEL_ANIMATION_MS,
  fallbackWidthPx: FALLBACK_WIDTH_PX,
} = SHOPPING_ROW_SWIPE;

/**
 * Per-row swipe-to-action for shopping list items, sharing the exact physics of
 * the family-wide row gesture (deadzone, axis-lock, rubber-band, fling, rising
 * haptic at the commit threshold).
 *   - Swipe right past the threshold → toggle "bought".
 *   - Swipe left  past the threshold → delete the item.
 * Drags that begin on a control (checkbox / delete button) are ignored so the
 * keyboard / click paths stay intact.
 */
export function useShoppingItemSwipe(options: SwipeGesturesOptions) {
  const [tx, setTx] = createSignal(0);
  const [isSwiping, setIsSwiping] = createSignal(false);
  const [isAnimating, setIsAnimating] = createSignal(false);

  let hasSwiped = false;

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: THRESHOLD_PX,
    commitPx: COMMIT_THRESHOLD_PX,
    haptics: true,
    hapticMs: HAPTIC_VIBRATION_MS,
    canStart: ({ target }) => !closestSelector(target, 'button, a, input, [data-no-swipe="true"]'),
    onStart: () => {
      setIsSwiping(true);
      setIsAnimating(false);
    },
    onMove: (sample) => {
      setTx(applyRubberBand(sample.dx, RUBBER_BAND_LIMIT, RUBBER_BAND_FACTOR));
    },
    onEnd: () => {
      hasSwiped = true;
      setIsSwiping(false);
      setIsAnimating(true);

      const finalTx = tx();
      const surfaceRef = options.getSurfaceRef();
      const width = surfaceRef?.clientWidth ?? FALLBACK_WIDTH_PX;
      const currentItem = options.item();

      if (finalTx > COMMIT_THRESHOLD_PX) {
        // Swipe right: toggle bought, then settle back to center.
        const nextVal = !options.isChecked();
        options.onToggle(currentItem, nextVal);
        setTx(0);
        setTimeout(() => {
          setIsAnimating(false);
        }, CANCEL_ANIMATION_MS);
      } else if (finalTx < -COMMIT_THRESHOLD_PX) {
        // Swipe left: delete (slide the row fully out first).
        setTx(-width);
        setTimeout(() => {
          options.onDelete(currentItem.id);
          setTx(0);
          setIsAnimating(false);
        }, COMMIT_ANIMATION_MS);
      } else {
        // Cancel and bounce back to center.
        setTx(0);
        setTimeout(() => {
          setIsAnimating(false);
        }, CANCEL_ANIMATION_MS);
      }
    },
    onCancel: () => {
      setIsSwiping(false);
      setIsAnimating(true);
      setTx(0);
      setTimeout(() => {
        setIsAnimating(false);
      }, CANCEL_ANIMATION_MS);
    },
  });

  const base = pointerHandlers(recognizer);

  return {
    tx,
    isSwiping,
    isAnimating,
    handlePointerDown: base.onPointerDown,
    handlePointerMove: base.onPointerMove,
    handlePointerUp: base.onPointerUp,
    handlePointerCancel: base.onPointerCancel,
    getHasSwiped: () => hasSwiped,
    resetHasSwiped: () => {
      hasSwiped = false;
    },
    COMMIT_THRESHOLD_PX,
  };
}
